import { useEffect, useState } from 'react'
import { SurvivorRow } from '../components/SurvivorRow'
import { getMaxSurvivors } from '../game/lodging'
import { useI18n } from '../i18n/useI18n'
import { useGameStore } from '../store/useGameStore'
import { SurvivorDetailScreen } from './SurvivorDetailScreen'

export function SurvivorsScreen({ forceReset }: { forceReset?: number }) {
  const { t } = useI18n()
  const survivors = useGameStore((s) => s.survivors)
  const workstations = useGameStore((s) => s.workstations)
  const rosterCap = getMaxSurvivors(workstations)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [now, setNow] = useState(() => Date.now())

  const workstationAssignedIds = new Set(
    Object.values(workstations)
      .filter((ws) => ws?.assignedSurvivorId)
      .map((ws) => ws!.assignedSurvivorId!),
  )

  useEffect(() => {
    if (!forceReset) return
    setSelectedId(null)
  }, [forceReset])

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [])

  if (selectedId) {
    return (
      <SurvivorDetailScreen
        survivorId={selectedId}
        onBack={() => setSelectedId(null)}
      />
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-end justify-between gap-2">
        <h2 className="mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">
          {t('surv_title')}
        </h2>
        <span className="mb-1 shrink-0 text-[11px] font-medium tabular-nums text-stone-400">
          {t('surv_roster_cap', { current: survivors.length, max: rosterCap })}
        </span>
      </div>
      <div data-tutorial-id="survivor-list" className="space-y-2">
        {survivors.map((s) => {
          const isWorking = workstationAssignedIds.has(s.id)
          return (
            <SurvivorRow
              key={s.id}
              survivor={s}
              onClick={setSelectedId}
              statusLabelOverride={
                s.status === 'Injured'
                  ? `${t('status_injured')} ${Math.max(
                      1,
                      Math.ceil(Math.max(0, (s.injuryEndsAt ?? now) - now) / 60_000),
                    )}m`
                  : isWorking
                    ? t('status_working')
                    : undefined
              }
              statusToneOverride={
                isWorking ? 'bg-sky-950 text-sky-300 ring-sky-800' : undefined
              }
            />
          )
        })}
      </div>
      <p className="px-1 pt-1 text-[11px] italic leading-relaxed text-stone-500 text-center">
        {t('surv_hint')}
      </p>
    </div>
  )
}
